<template>
  <ClientOnly>
    <div
      class="ui-modal-dialog"
      :class="[
        isFullSize ? 'is-full-size' : 'is-custom-size',
        `has-bg-${hasBgColor}`,
        {
          'is-transparent-header': appearance?.header?.isTransparent,
        }
      ]"
      :style="[isFullSize
        ? {
          width: '100vw',
          height: supportsDvh ? '100dvh' : '100vh',
        }
        : {
          width: `min(${dialogWidth}, ${fullWidth})`,
          height: `min(${dialogHeight}, ${fullHeight})`,
          minWidth: `min(${dialogMinWidth}, ${fullWidth})`,
          minHeight: `min(${dialogMinHeight}, ${fullHeight})`,
          maxWidth: !dialogWidth && !dialogMaxWidth
            ? fullWidth
            : `min(${dialogMaxWidth}, ${fullWidth})`,
          maxHeight: !dialogHeight && !dialogMaxHeight
            ? fullHeight
            : `min(${dialogMaxHeight}, ${fullHeight})`,
        }]"
    >
      <!-- Modal header -->
      <header
        v-if="!noHeader"
        class="ui-modal-dialog-header"
      >
        <slot name="header-area">
          <h2 v-if="$slots.header" class="layout-row h4">
            <slot name="header" />
          </h2>
          <slot name="header-right">
            <button
              v-if="!noCloser"
              type="button"
              class="ui-modal-dialog-closer"
              @click="reject()"
            >
              <BaseIcon name="mdi/close-circle-outline" size="size-2" />
              <span class="u-screen-reader-only">{{ lss('閉じる', 'Close') }}</span>
            </button>
          </slot>
        </slot>
      </header>
      <slot name="header-extra" />
      <!-- Modal body -->
      <div class="ui-modal-dialog-body">
        <slot :resolve="resolve" :reject="reject" />
      </div>
      <!-- Modal footer -->
      <footer v-if="!noFooter" class="ui-modal-dialog-footer">
        <div class="footer-extra">
          <slot name="footer-extra" />
        </div>
        <div
          class="footer-buttons"
        >
          <slot name="buttons" :resolve="resolve" :reject="reject"> </slot>
        </div>
      </footer>
      <slot name="modal-overlay" />
    </div>
  </ClientOnly>
</template>

<script lang="ts" setup>
// Injectするために、BaseModalの下に入れて使う

const { lss } = useI18n()

interface Props {
  noHeader?: boolean
  noFooter?: boolean
  noCloser?: boolean
  dialogWidth?: string
  dialogHeight?: string
  dialogMinWidth?: string
  dialogMinHeight?: string
  dialogMaxWidth?: string
  dialogMaxHeight?: string
  isFullSize?: boolean
  hasBgColor?: 'gray' | 'white'
  appearance?: {
    header?: {
      isTransparent?: boolean,
    }
  }
}

const props = withDefaults(defineProps<Props>(), {
  noHeader: false,
  noFooter: false,
  hasBgColor: 'white',
})

const reject = inject('reject') as () => void
const resolve = inject('resolve') as (payload?: any) => void

const supportsDvh = computed(() => {
  return typeof CSS !== 'undefined' && CSS.supports('height', '100dvh')
})

const fullWidth = ref(supportsDvh.value ? 'calc(100dvw - 2rem)' : 'calc(100vw - 2rem)')
const fullHeight = ref(supportsDvh.value ? 'calc(100dvh - 2rem)' : 'calc(100vh - 2rem)')
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.footer-buttons {
  display: flex;
  height: 3rem;
  align-items: center;
  gap: 1px;
  border-top: 1px solid $jps-gray-300;
  background-color: $jps-gray-200;
  :deep(button) {
    cursor: pointer ;
    flex-grow: 1;
    height: 100%;
    background-color: $white;
    text-align: center;
    font-weight: 500;
    color: $jps-key-blue;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-timing-function: cubic-bezier(0.64, 0.09, 0.08, 1);
    transition-duration: 250ms;
    &:hover {
      background-color: $jps-gray-300;
    }
    &:disabled {
      cursor: not-allowed;
      background-color: $jps-gray-100;
      opacity: .5;
    }
  }
}

.ui-modal-dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  background-color: $white;

  &.is-full-size {}

  &.is-custom-size {
    margin: 1rem 0;
    border-radius: 1rem;
  }

  &.has-bg-white {
  }

  &.has-bg-gray {
    background-color: $jps-gray-50;
  }

  &.is-transparent-header {
    .ui-modal-dialog-header {
      background-color: transparent;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      z-index: 2;
    }

    .ui-modal-dialog-body {
      padding-top: 4rem;
    }
  }
}
.ui-modal-dialog-header {
    display: flex;
    min-height: 4rem;
    align-items: center;
    justify-content: space-between;
    border-bottom-width: 1px;
    border-color: $jps-gray-200;
    padding-left: 1rem;
    padding-right: 1rem;
    @include is-tablet {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
    }
    h2.layout-row {
      :deep(svg) {
        margin-right: .5rem; // SVGアイコン左に余白
      }
    }
}
.ui-modal-dialog-closer {
    margin-inline-start: auto;
    display: inline-flex;
    //height: 2rem;
    //width: 2rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background-color: transparent;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: $jps-gray-400;
    cursor: pointer;

    @include transition-all;

    &:hover {
      background-color: $jps-gray-200;
      color: $jps-gray-900;
    }
}
.ui-modal-dialog-body {
    flex-grow: 1;
    overflow-y: auto;
    padding: 1rem;
    font-size: $size-7;
    & > :not(:last-child) {
      margin-block-start: 0;
      margin-block-end: 16px;
    };
    @include is-tablet {
      padding: 1.25rem;
    }
}
.ui-modal-dialog-footer {
    display: flex;
    flex-direction: column;
    box-shadow: inset 0 1px #83838340;
}
.footer-extra {
    &:empty { display: none; }
}
</style>
